// Orchestrator modal: generate PDF, upload, preview, copy link, send WhatsApp.
// Includes confirmation modal before opening WhatsApp.

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  X, Download, Copy, MessageCircle, Loader2, Eye, FileText, Link2, CheckCircle2,
} from "lucide-react";

import { useLang } from "@/contexts/LangContext";
import { formatSAR, formatDate } from "@/lib/api";
import InvoiceTemplate from "@/components/InvoiceTemplate";
import { generatePdfBlob, downloadBlob } from "@/services/InvoicePdfService";
import { uploadInvoicePdf, copyToClipboard } from "@/services/InvoiceShareService";
import {
  formatPhoneInternational, buildInvoiceMessage, openWhatsapp,
} from "@/services/WhatsappService";

export default function InvoiceShareModal({ tx, customerName = "", paymentMethod = "Tunai", onClose }) {
  const { t, lang } = useLang();
  const sheetRef = useRef(null);

  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("idle"); // idle | generating | uploading | done | error
  const [invoiceUrl, setInvoiceUrl] = useState("");
  const [pdfBlob, setPdfBlob] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const invoiceNo = (tx?.id || "").slice(0, 8).toUpperCase();
  const filename = `Invoice_JavaSPA_${invoiceNo}.pdf`;
  const phoneIntl = formatPhoneInternational(tx?.customer_phone);

  // Auto-prepare PDF + URL on mount so preview QR is correct
  useEffect(() => {
    let cancelled = false;
    async function prepare() {
      try {
        setStage("generating");
        // Render first pass without QR (placeholder) to capture, then we re-render
        // Simpler: just upload twice OR generate after we have URL.
        // Strategy: upload a placeholder generated PDF first to obtain URL, then
        // re-render with the real URL and upload again with same id is not
        // feasible. Instead: upload first (with empty url), get URL, regenerate.
        // To keep simple: generate PDF with a tentative URL based on backend
        // returning unique id BEFORE upload? Not possible — id is assigned on
        // upload.
        // Workaround: pre-upload a 1-byte placeholder to grab the URL pattern?
        // Cleanest UX: generate twice — once without QR (for upload to get
        // URL), once with QR (final).

        // PASS 1 — render template without invoiceUrl (no QR)
        await new Promise((r) => setTimeout(r, 100));
        if (cancelled) return;
        const firstBlob = await generatePdfBlob(sheetRef.current);
        if (cancelled) return;
        setStage("uploading");
        const { url } = await uploadInvoicePdf(firstBlob, tx.id, filename);
        if (cancelled) return;
        setInvoiceUrl(url);

        // PASS 2 — wait for re-render with URL → regenerate PDF + replace
        // (replace on server side is overwrite-on-id; we'll just upload again
        // and use the second URL since each upload yields a new id)
        await new Promise((r) => setTimeout(r, 250));
        if (cancelled) return;
        const secondBlob = await generatePdfBlob(sheetRef.current);
        if (cancelled) return;
        const { url: finalUrl } = await uploadInvoicePdf(secondBlob, tx.id, filename);
        if (cancelled) return;
        setInvoiceUrl(finalUrl);
        setPdfBlob(secondBlob);
        setStage("done");
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setErrorMsg(err?.response?.data?.detail || err?.message || "PDF generation failed");
        setStage("error");
        toast.error(t("share.pdfFail"));
      }
    }
    prepare();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tx?.id]);

  function handleDownload() {
    if (!pdfBlob) {
      toast.error(t("share.pdfNotReady"));
      return;
    }
    downloadBlob(pdfBlob, filename);
  }

  async function handleCopyLink() {
    if (!invoiceUrl) {
      toast.error(t("share.linkNotReady"));
      return;
    }
    const ok = await copyToClipboard(invoiceUrl);
    if (ok) toast.success(t("share.linkCopied"));
    else toast.error(t("share.linkCopyFail"));
  }

  function handleAskSendWA() {
    if (!tx?.customer_phone || !phoneIntl) {
      toast.error(t("share.noPhone"));
      return;
    }
    if (!invoiceUrl) {
      toast.error(t("share.linkNotReadyWait"));
      return;
    }
    setConfirmOpen(true);
  }

  function handleConfirmSendWA() {
    const msg = buildInvoiceMessage({
      customerName,
      totalAmount: formatSAR(tx.total),
      transactionDate: formatDate(tx.created_at),
      invoiceUrl,
      lang,
    });
    openWhatsapp(phoneIntl, msg);
    setConfirmOpen(false);
    toast.success(t("share.sentSuccess"));
  }

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="invoice-share-modal"
    >
      {/* Top action bar */}
      <div
        className="print-hide sticky top-0 z-[100] bg-charcoal-900/90 backdrop-blur border-b border-gold/20 px-4 py-3 flex flex-wrap items-center justify-between gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 text-sm text-gold font-medium">
          <FileText className="h-4 w-4" />
          {t("share.invoice")} · #{invoiceNo}
          {stage === "done" && (
            <span className="text-xs text-leaf inline-flex items-center gap-1 ms-2">
              <CheckCircle2 className="h-3.5 w-3.5" /> {t("share.ready")}
            </span>
          )}
          {(stage === "generating" || stage === "uploading") && (
            <span className="text-xs text-foreground/60 inline-flex items-center gap-1 ms-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {stage === "generating" ? t("share.generating") : t("share.uploading")}
            </span>
          )}
          {stage === "error" && (
            <span className="text-xs text-red-400 ms-2">{t("share.error")}: {errorMsg}</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleDownload}
            disabled={stage !== "done"}
            data-testid="invoice-download-btn"
            className="btn-outline-gold py-2 px-3 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download className="h-3.5 w-3.5" /> {t("share.downloadPdf")}
          </button>
          <button
            onClick={handleCopyLink}
            disabled={stage !== "done"}
            data-testid="invoice-copy-btn"
            className="btn-outline-gold py-2 px-3 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Link2 className="h-3.5 w-3.5" /> {t("share.copyLink")}
          </button>
          <button
            onClick={handleAskSendWA}
            disabled={stage !== "done"}
            data-testid="invoice-send-wa-btn"
            className="inline-flex items-center gap-1.5 py-2 px-3 rounded-full bg-leaf hover:bg-leaf-light text-white font-medium text-xs transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <MessageCircle className="h-3.5 w-3.5" /> {t("share.sendWa")}
          </button>
          <button
            onClick={onClose}
            data-testid="invoice-share-close-btn"
            className="p-2 rounded-full hover:bg-white/5"
            aria-label={t("share.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Hint bar */}
      <div className="px-4 py-2 text-[11px] text-foreground/60 bg-charcoal-900/50 border-b border-gold/10 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <Eye className="h-3 w-3" /> {t("share.previewHint")}
        {invoiceUrl && (
          <span className="ms-auto text-leaf truncate max-w-[50%]" title={invoiceUrl}>
            {invoiceUrl}
          </span>
        )}
      </div>

      {/* Loading spinner overlay when no preview yet */}
      {stage !== "done" && stage !== "error" && (
        <div className="text-center py-8 text-foreground/60" onClick={(e) => e.stopPropagation()}>
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          <div className="mt-3 text-sm">{t("share.preparing")}</div>
        </div>
      )}

      {/* PRINTABLE A4 PREVIEW */}
      <div className="grid place-items-center p-4 md:p-8" onClick={(e) => e.stopPropagation()}>
        <div ref={sheetRef} data-print="report" className="shadow-2xl">
          <InvoiceTemplate
            tx={tx}
            invoiceUrl={invoiceUrl}
            paymentMethod={paymentMethod}
            customerName={customerName}
          />
        </div>
      </div>

      {/* CONFIRM SEND WA MODAL */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-[110] bg-black/85 grid place-items-center p-4"
          onClick={() => setConfirmOpen(false)}
          data-testid="invoice-confirm-modal"
        >
          <div
            className="card-luxury max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="h-10 w-10 rounded-xl bg-leaf/15 text-leaf grid place-items-center shrink-0">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-heading text-lg">{t("share.confirmTitle")}</h3>
                <p className="text-xs text-foreground/60 mt-1">
                  {t("share.confirmDesc")}{" "}
                  <span className="text-gold font-medium">{tx.customer_phone}</span>
                </p>
              </div>
            </div>

            <div className="bg-charcoal-900/60 border border-gold/10 rounded-lg p-3 mb-4 text-xs text-foreground/80 max-h-48 overflow-auto whitespace-pre-line font-mono">
              {buildInvoiceMessage({
                customerName,
                totalAmount: formatSAR(tx.total),
                transactionDate: formatDate(tx.created_at),
                invoiceUrl,
                lang,
              })}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                data-testid="invoice-confirm-cancel"
                className="btn-outline-gold justify-center"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={handleConfirmSendWA}
                data-testid="invoice-confirm-send"
                className="inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-leaf hover:bg-leaf-light text-white font-medium transition"
              >
                <MessageCircle className="h-4 w-4" /> {t("share.openWa")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
